import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import SecondaryButton from "@/Components/UI/Forms/SecondaryButton";
import TextAreaInput from "@/Components/UI/Forms/TextAreaInput";
import Modal from "@/Components/UI/Modal";
import { Link } from "@inertiajs/react";
import axios from "axios";
import { useState } from "react";
import { FaCheck } from "react-icons/fa6";

const MAX_LENGTH = 4000;

type ShareFeedbackModalProps = {
    show: boolean;
    onRequestClose: () => void;
};

export default function ShareFeedbackModal({
    show,
    onRequestClose,
}: ShareFeedbackModalProps) {
    const [whatsWorking, setWhatsWorking] = useState("");
    const [whatToImprove, setWhatToImprove] = useState("");
    const [mode, setMode] = useState<"form" | "thanks">("form");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const canSubmit =
        !submitting &&
        (whatsWorking.trim().length > 0 || whatToImprove.trim().length > 0);

    const close = () => {
        if (submitting) {
            return;
        }
        onRequestClose();
        setMode("form");
        setWhatsWorking("");
        setWhatToImprove("");
        setSubmitError(null);
    };

    const submit = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            await axios.post(route("client.feedback.store"), {
                whats_working: whatsWorking,
                what_to_improve: whatToImprove,
            });
            setMode("thanks");
        } catch {
            setSubmitError(
                "Something went wrong sending your feedback. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            id="share-feedback-modal"
            testId="share-feedback-modal"
            onClose={close}
            show={show}
            maxWidth="2xl"
            showCloseButton={true}
            closeable={!submitting}
        >
            {mode === "form" && (
                <div className="flex flex-col gap-6 p-8 text-brand-black">
                    <h2 data-testid="title" className="text-2xl font-bold">
                        Share feedback
                    </h2>

                    <div className="flex flex-col gap-3 rounded-md bg-brand-lightGray p-4 text-sm">
                        <p>
                            Thank you in advance for providing feedback about
                            the Strawberry.me platform. While we strive to read
                            every response, we are not able to individually
                            respond to all feedback.
                        </p>
                        <p>
                            If you need a response or any action to be taken on
                            your account, please{" "}
                            <Link
                                href={route("contact")}
                                data-testid="contact-us-link"
                                onClick={close}
                                className="text-brand-red underline"
                            >
                                contact us
                            </Link>{" "}
                            instead.
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label
                            htmlFor="share-feedback-whats-working"
                            className="font-medium text-lg"
                        >
                            What's working well?
                        </label>
                        <TextAreaInput
                            id="share-feedback-whats-working"
                            data-testid="whats-working-input"
                            placeholder="I like how..."
                            maxLength={MAX_LENGTH}
                            value={whatsWorking}
                            onChange={(e) => setWhatsWorking(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label
                            htmlFor="share-feedback-what-to-improve"
                            className="font-medium text-lg"
                        >
                            What could be better?
                        </label>
                        <TextAreaInput
                            id="share-feedback-what-to-improve"
                            data-testid="what-to-improve-input"
                            placeholder="Some things to improve on are..."
                            maxLength={MAX_LENGTH}
                            value={whatToImprove}
                            onChange={(e) => setWhatToImprove(e.target.value)}
                        />
                    </div>

                    {submitError && (
                        <p role="alert" className="text-sm text-brand-red">
                            {submitError}
                        </p>
                    )}

                    <div className="flex flex-col gap-3 sm:flex-row">
                        <PrimaryButton
                            type="button"
                            data-testid="submit-button"
                            disabled={!canSubmit}
                            onClick={submit}
                            className="grow justify-center py-3 sm:order-2 sm:w-0"
                        >
                            {submitting ? "Sending..." : "Send Feedback"}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            data-testid="cancel-button"
                            disabled={submitting}
                            onClick={close}
                            className="grow justify-center py-3 sm:w-0"
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </div>
            )}

            {mode === "thanks" && (
                <div className="flex flex-col items-center gap-6 p-8 text-center text-brand-black">
                    <div className="relative bg-brand-decoBlue/40 rounded-full p-3">
                        <FaCheck size={24} />
                        <div className="absolute top-0 left-0 w-full h-full bg-brand-decoBlue/20 rounded-full animate-ping-slow" />
                    </div>
                    <h2
                        data-testid="thanks-title"
                        className="text-2xl font-bold"
                    >
                        Thanks for sharing
                    </h2>
                    <p className="text-lg">
                        We read every response. Even though we can't reply
                        individually, your feedback helps us improve.
                    </p>
                    <p className="text-lg">
                        If you need a response or any action to be taken on your
                        account, please{" "}
                        <Link
                            href={route("contact")}
                            data-testid="contact-us-link"
                            onClick={close}
                            className="text-brand-red underline"
                        >
                            contact us
                        </Link>{" "}
                        instead.
                    </p>
                    <PrimaryButton
                        type="button"
                        data-testid="done-button"
                        onClick={close}
                        className="w-full justify-center py-3"
                    >
                        Done
                    </PrimaryButton>
                </div>
            )}
        </Modal>
    );
}
